import { Box, Tooltip, Typography } from '@mui/material';
import { CallActors } from '@shared/constants/recipient-status.constants';
import { RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConversationMinimapMessage {
  id: string;
  actor: string;
  message?: string;
}

const MAX_MINIMAP_ITEMS = 30;

const sampleMessages = (messages: ConversationMinimapMessage[]) => {
  if (messages.length <= MAX_MINIMAP_ITEMS) return messages;

  const step = (messages.length - 1) / (MAX_MINIMAP_ITEMS - 1);

  return Array.from(
    { length: MAX_MINIMAP_ITEMS },
    (_, index) => messages[Math.round(index * step)],
  );
};

interface ConversationMinimapProps {
  messages: ConversationMinimapMessage[];
  messageRefs: RefObject<Record<string, HTMLDivElement | null>>;
  messagesBoxRef: RefObject<HTMLDivElement | null>;
}

export const ConversationMinimap = ({
  messages,
  messageRefs,
  messagesBoxRef,
}: ConversationMinimapProps) => {
  const { t } = useTranslation();
  const visibleMessages = sampleMessages(messages);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    const messagesBox = messagesBoxRef.current;
    if (!messagesBox) return;

    let frameId: number | null = null;

    const updateActiveIndex = () => {
      const viewportCenter = messagesBox.scrollTop + messagesBox.clientHeight / 2;
      const messagesBoxTop = messagesBox.getBoundingClientRect().top;
      let nearestIndex: number | null = null;
      let nearestDistance = Number.POSITIVE_INFINITY;

      visibleMessages.forEach((message, index) => {
        const element = messageRefs.current[message.id];
        if (!element) return;

        const elementRect = element.getBoundingClientRect();
        const messageCenter =
          messagesBox.scrollTop + elementRect.top - messagesBoxTop + elementRect.height / 2;
        const distance = Math.abs(messageCenter - viewportCenter);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      setActiveIndex(nearestIndex);
    };

    const scheduleUpdate = () => {
      if (frameId !== null) return;

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateActiveIndex();
      });
    };

    messagesBox.addEventListener('scroll', scheduleUpdate, { passive: true });
    scheduleUpdate();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
    resizeObserver?.observe(messagesBox);

    return () => {
      messagesBox.removeEventListener('scroll', scheduleUpdate);
      resizeObserver?.disconnect();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [messages, messageRefs, messagesBoxRef]);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 6,
        top: '50%',
        height: 140,
        transform: 'translateY(-50%)',
        width: 12,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        zIndex: 1,
      }}
      role="group"
      aria-label={t('campaignPage.conversationMessageOverview')}>
      {visibleMessages.map((message, index) => {
        const isAgent = message.actor === CallActors.AGENT;
        const focusedIndex = hoveredIndex ?? activeIndex;
        const distance = focusedIndex === null ? null : Math.abs(index - focusedIndex);
        const hoverRadius = 5;
        const scale =
          distance !== null && distance <= hoverRadius
            ? 1 + (2.5 - 1) * (1 - distance / (hoverRadius + 1))
            : 1;

        return (
          <Tooltip
            key={message.id}
            placement="left"
            arrow
            title={
              <Box sx={{ maxWidth: 260, p: 1.5 }}>
                <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                  {t(
                    isAgent
                      ? 'campaignPage.callDetailHeader.agent'
                      : 'campaignPage.callDetailHeader.customer',
                  )}
                </Typography>
                <Typography variant="body2">
                  {message.message ?? t('campaignPage.toolCall.executed')}
                </Typography>
              </Box>
            }
            slotProps={{ tooltip: { sx: { p: 0 } } }}>
            <Box
              component="button"
              type="button"
              aria-label={t('campaignPage.toolCall.navigateToMessage', {
                message: message.message ?? t('campaignPage.toolCall.executed'),
              })}
              onClick={() =>
                messageRefs.current[message.id]?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                })
              }
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              sx={{
                border: 0,
                borderRadius: 1,
                cursor: 'pointer',
                flex: 1,
                minHeight: 0,
                width: isAgent ? 12 : 8,
                p: 0,
                backgroundColor: 'transparent',
                position: 'relative',
                opacity: isAgent ? 0.75 : 1,
                transform: `scaleX(${scale})`,
                transformOrigin: 'left center',
                transition: 'transform 140ms linear',
                zIndex: 1,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '50%',
                  height: 2,
                  borderRadius: 1,
                  transform: 'translateY(-50%)',
                  backgroundColor: isAgent ? 'primary.main' : 'text.disabled',
                },
                '&:hover': {
                  opacity: 1,
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};
